"use client";

import Image from "next/image";
import { useState } from "react";
import { Play } from "lucide-react";

type Props = {
  /** Any YouTube URL — watch, youtu.be short link, or an /embed/ URL. */
  url: string;
  poster?: string | null;
  title: string;
};

/**
 * Extracts the YouTube video id from the URL shapes the CMS stores
 * (`youtu.be/ID`, `watch?v=ID`, `/embed/ID`). Returns null for anything
 * else so the caller can skip rendering rather than emit a broken iframe.
 */
function youtubeId(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.endsWith("youtu.be")) {
      return parsed.pathname.slice(1) || null;
    }
    if (parsed.hostname.endsWith("youtube.com")) {
      const v = parsed.searchParams.get("v");
      if (v) return v;
      const match = parsed.pathname.match(/\/embed\/([^/?]+)/);
      if (match) return match[1];
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Trip video — matches production's real "#video" section: a 16:9 poster
 * frame with a centred play button that swaps in the YouTube player on
 * click. Click-to-load keeps YouTube's scripts and cookies off the page
 * until the visitor actually asks for the video.
 */
export function VideoEmbed({ url, poster, title }: Props) {
  const [playing, setPlaying] = useState(false);
  const id = youtubeId(url);
  if (!id) return null;

  return (
    <figure className="relative w-full overflow-hidden rounded bg-black">
      <div className="relative aspect-video w-full">
        {playing ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 h-full w-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${title}`}
            className="group absolute inset-0 h-full w-full"
          >
            {poster ? (
              <Image
                src={poster}
                alt={title}
                fill
                sizes="(min-width: 1024px) 720px, 100vw"
                className="object-cover"
              />
            ) : null}
            <span className="absolute inset-0 grid place-items-center">
              <span className="inline-grid h-16 w-16 place-items-center rounded-full bg-primary text-white shadow-lg transition-transform group-hover:scale-105">
                <Play size={26} fill="currentColor" aria-hidden />
              </span>
            </span>
          </button>
        )}
      </div>
    </figure>
  );
}
